// src/lib/dayjs.js
import dayjs from 'dayjs';

// Only plugins you need
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import minMax from 'dayjs/plugin/minMax';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(minMax);
dayjs.extend(advancedFormat);
dayjs.extend(isBetween);
dayjs.extend(isLeapYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// Optional: set default tz if you want consistent display
// dayjs.tz.setDefault('America/Chicago');

/**
 * Day count fraction between two dates under common conventions.
 * @param {Date|string|dayjs} d1 - start date
 * @param {Date|string|dayjs} d2 - end date
 * @param {string} convention - "30/360", "ACT/360", "ACT/ACT"
 * @returns {number} fraction of a year
 */
export function dayCountFraction(d1, d2, convention = '30/360') {
  const start = dayjs(d1);
  const end = dayjs(d2);

  if (!start.isValid() || !end.isValid()) {
    throw new Error('Invalid date(s) passed to dayCountFraction');
  }

  switch (convention.toUpperCase()) {
    case '30/360': {
      // US 30/360 (Bond Basis)
      let d1Day = start.date();
      let d2Day = end.date();

      if (d1Day === 31) d1Day = 30;
      if (d2Day === 31 && d1Day === 30) d2Day = 30;

      const years = end.year() - start.year();
      const months = end.month() - start.month();
      const days = d2Day - d1Day;

      return (360 * years + 30 * months + days) / 360.0;
    }

    case 'ACT/360': {
      const days = end.diff(start, 'day');
      return days / 360.0;
    }

    case 'ACT/ACT': {
      // ISDA-style Actual/Actual: split by calendar year
      let fraction = 0;
      let current = start;

      while (current.isBefore(end)) {
        const yearEnd = dayjs(new Date(current.year(), 11, 31));
        const periodEnd = end.isBefore(yearEnd) ? end : yearEnd;

        let daysInPeriod;
        if (periodEnd.isSame(end)) {
          // Final partial period → diff is exclusive, no +1
          daysInPeriod = periodEnd.diff(current, 'day');
        } else {
          // Year-end split → include Dec 31
          daysInPeriod = periodEnd.diff(current, 'day') + 1;
        }

        const daysInYear = current.isLeapYear() ? 366 : 365;
        fraction += daysInPeriod / daysInYear;

        current = periodEnd.add(1, 'day'); // move to next segment
      }

      return fraction;
    }

    default:
      throw new Error(`Unsupported day count convention: ${convention}`);
  }
}

export default dayjs;
